import React from 'react';
import { Button, ButtonToolbar, CardBody, FormGroup, Input } from 'reactstrap';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import LoadingIcon from 'mdi-react/LoadingIcon';
import TablePagination from '@material-ui/core/TablePagination';
import { defaultPaginationProps } from '@/shared/helpers';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TableHead from '@material-ui/core/TableHead';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import api from '@/utils/apiClient';
import { isEmpty } from 'lodash/lang';

const AssessmentColumns = [
  {
    id: 'checkbox',
    disablePadding: true,
    label: '',
  },
  {
    id: 'title',
    disablePadding: true,
    label: 'Assessment Name',
  },
  {
    id: 'category',
    disablePadding: true,
    label: 'Assessment Category',
  },
];

const AssessmentTableSearchBar = React.memo(({ totalAssessment, filters, setFilters }) => {
  const [isSearched, setIsSearched] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const handleSearch = () => {
    setIsSearched(true);
    setFilters({ ...filters, search, pageNumber: 1 });
  };

  const handleClear = () => {
    setSearch('');
    setIsSearched(false);
    setFilters({ ...filters, search: '' });
  };

  return (
    <TableHead>
      <div className="table__search">
        <div className="table__search_item">
          <b>Assessment Name</b>
          <div className="form">
            <div className="form__form-group">
              <div className="form__form-group-field">
                <div className="form__form-group-icon" style={{ height: 37 }}>
                  <MagnifyIcon />
                </div>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="search-bar-search-text"
                  name="searchText"
                  type="text"
                  placeholder="Search by Assessment Name"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="table__search_item mt-3">
          <Button color="primary" type="button" onClick={handleSearch} outline>
            {' '}
            Search{' '}
          </Button>
        </div>
        <div className="table__search_item mt-3">
          <Button color="secondary" onClick={handleClear} outline>
            Clear
          </Button>
        </div>
      </div>

      {isSearched && (
        <div className="ml-1">
          <span>Found {totalAssessment} matches</span>
        </div>
      )}
    </TableHead>
  );
});

const AssessmentTableColumns = React.memo(({ order, orderBy, onRequestSort }) => {
  return (
    <TableRow>
      {AssessmentColumns.map((row) => (
        <TableCell
          className="material-table__cell material-table__cell--sort material-table__cell-center"
          key={row.id}
          align={'center'}
          padding={row.disablePadding ? 'none' : 'default'}
          sortDirection={orderBy === row.id ? order : false}
        >
          <TableSortLabel
            active={orderBy === row.id}
            direction={order}
            onClick={(event) => onRequestSort(event, row.id)}
            className="material-table__sort-label"
            dir="ltr"
          >
            <b>{row.label}</b>
          </TableSortLabel>
        </TableCell>
      ))}
    </TableRow>
  );
});

const AssessmentTableRows = React.memo(
  ({ assessments, checkedAssessment, setCheckedAssessment }) => {
    return assessments?.map((assessment) => (
      <TableRow className="material-table" tabIndex={-1} key={assessment.id}>
        <TableCell align="center" className="material-table__cell">
          <FormGroup>
            <Input
              className="table-checkbox"
              type="checkbox"
              value={assessment.id}
              checked={checkedAssessment?.id === assessment.id}
              onChange={() => setCheckedAssessment(assessment)}
            />
          </FormGroup>
        </TableCell>
        <TableCell align="center" className="material-table__cell">
          {assessment?.title}
        </TableCell>
        <TableCell align="center" className="material-table__cell">
          {assessment?.category}
        </TableCell>
      </TableRow>
    ));
  },
);

const StepTwo = ({
  nextPage,
  previousPage,
  checkedCustomer,
  checkedAssessment,
  setCheckedAssessment,
}) => {
  const [state, setState] = React.useState({
    loading: false,
    message: '',
    isError: false,
    isSuccess: false,
    assessments: [],
    totalRecords: 0,
  });
  const [filters, setFilters] = React.useState({
    pageSize: 10,
    pageNumber: 1,
    sortBy: '',
    isSortOrderDescending: false,
    search: '',
  });
  const {
    pageSize: rowsPerPage,
    pageNumber: page,
    sortBy: orderBy,
    isSortOrderDescending,
  } = filters;
  const { assessments, totalRecords, loading, isSuccess, isError, message } = state;

  const getAssessmentListForAssignment = async (filters) => {
    const defaultError = 'Error occurred getting assessments list';
    let updatedState = {};
    try {
      setState({ ...state, loading: true });
      const { data: res, isSuccess } = await api.assessments.getAssessmentList({
        ...filters,
        organizationId: checkedCustomer.organizationId,
      });
      if (isSuccess)
        updatedState = {
          ...state,
          isSuccess: true,
          isError: false,
          assessments: res.data.records,
          totalRecords: res.data.totalRecords,
        };
      else throw new Error(res?.data?.messages?.[0] ?? defaultError);
    } catch (error) {
      updatedState = {
        ...state,
        isSuccess: false,
        isError: true,
        assessments: [],
        totalRecords: 0,
      };
      console.error(defaultError, error.message);
    } finally {
      setState({ ...updatedState, loading: false });
    }
  };

  React.useEffect(() => {
    getAssessmentListForAssignment(filters);
  }, [filters]);

  const handleRequestSort = (event, property) => {
    setFilters({ ...filters, sortBy: property, isSortOrderDescending: !isSortOrderDescending });
  };

  const handleChangePage = (event, currentPage) => {
    setFilters({ ...filters, pageSize: rowsPerPage, pageNumber: currentPage + 1 });
  };

  const handleChangeRowsPerPage = (event) => {
    setFilters({ ...filters, pageSize: +event.target.value, pageNumber: 1 });
  };

  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - assessments.length : 0;

  return (
    <form className="form step-one" onSubmit={nextPage}>
      {/*Assessment Table*/}
      <CardBody className="col mt-4">
        <div className="material-table__wrap">
          <AssessmentTableSearchBar
            totalAssessment={totalRecords}
            setFilters={setFilters}
            filters={filters}
          />
          <Table className="material-table">
            <TableBody>
              <AssessmentTableColumns
                order={isSortOrderDescending ? 'desc' : 'asc'}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />
              {(loading || isError) && (
                <TableRow>
                  <TableCell colSpan={AssessmentColumns.length}>
                    {loading && (
                      <div className={'text-center overflow-hidden'}>
                        <div className="panel__refresh position-relative" style={{ height: 75 }}>
                          <LoadingIcon />
                        </div>
                      </div>
                    )}
                    <div className={'text-center'}>
                      <p className={isError ? 'm-5' : ''}>
                        {isError ? message : 'Loading Assessment...'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              <AssessmentTableRows
                assessments={assessments}
                checkedAssessment={checkedAssessment}
                setCheckedAssessment={setCheckedAssessment}
              />
              {emptyRows > 0 && (
                <TableRow style={{ height: 49 * emptyRows }}>
                  <TableCell colSpan={AssessmentColumns.length} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page - 1}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          component={defaultPaginationProps.component}
          backIconButtonProps={defaultPaginationProps.backIconButton}
          nextIconButtonProps={defaultPaginationProps.nextIconButton}
          rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
          className={defaultPaginationProps.className}
          dir={defaultPaginationProps.dir}
        />
      </CardBody>

      <br />

      <ButtonToolbar className="form__button-toolbar wizard__toolbar">
        <Button color="primary" type="button" onClick={previousPage} className="previous">
          Back
        </Button>
        <Button
          color="primary"
          type="button"
          onClick={nextPage}
          className="next"
          disabled={isEmpty(checkedAssessment)}
        >
          Next
        </Button>
      </ButtonToolbar>
    </form>
  );
};

export default StepTwo;
